import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PlaceIcon from '@mui/icons-material/Place';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InputBox from '../../atomic/inputbox/InputBox';
import { sharedInputSx } from '../Products/kitchenProductsConstants';
import { formatLastRefresh } from '../Products/kitchenProductsListHelpers';

const panelSx = {
  width: '100%',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.18)',
  overflow: 'hidden',
  pointerEvents: 'auto',
};

const inputSx = {
  width: '100%',
  ...sharedInputSx,
};

const multilineInputSx = {
  width: '100%',
  ...sharedInputSx,
  '& .MuiOutlinedInput-root': {
    ...sharedInputSx['& .MuiOutlinedInput-root'],
    height: 'auto',
    minHeight: '42px',
    alignItems: 'flex-start',
    '& textarea': {
      fontSize: '13px',
      color: '#3F3F6E',
    },
  },
};

export default function ZipcodeMapSearchPanel({
  searchMode,
  onSearchModeChange,
  zipInput,
  onZipInputChange,
  branchInput,
  onBranchInputChange,
  onSearch,
  onReload,
  onClear,
  loading,
  error,
  searchResults,
  notFoundZips,
  resolvedBy,
  mappedCount,
  lastFetchedAt,
  canReload,
  analyticsSummary,
  analyticsError,
  zipStatsMap,
}) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <Paper sx={panelSx} elevation={0}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: '#111827',
          color: '#fff',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 14 }}>
            Map Search
          </Typography>
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF', mt: 0.25 }}>
            ZIP codes or regional branch coverage
          </Typography>
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 10.5, color: '#6B7280', mt: 0.5 }}>
            Last fetch: {formatLastRefresh(lastFetchedAt)}
          </Typography>
        </Box>
        <Tooltip title="Reload from server (updates cache)" placement="left">
          <span>
            <IconButton
              size="small"
              onClick={onReload}
              disabled={loading || !canReload}
              aria-label="Reload map data"
              sx={{
                color: '#fff',
                mt: 0.25,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <RefreshIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ px: 2, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={searchMode}
          onChange={(_, value) => value && onSearchModeChange(value)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontFamily: 'Inter Regular, sans-serif',
              fontSize: 12,
              py: 0.65,
              borderColor: '#D7E0F2',
              color: '#3F3F6E',
              '&.Mui-selected': {
                bgcolor: '#fdeee7',
                color: '#3F3F6E',
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
              },
            },
          }}
        >
          <ToggleButton value="zip">ZIP codes</ToggleButton>
          <ToggleButton value="branch">Regional branch</ToggleButton>
        </ToggleButtonGroup>

        {searchMode === 'zip' ? (
          <InputBox
            type="text"
            label="ZIP codes"
            name="zipcodes"
            size="small"
            height="auto"
            value={zipInput}
            onChange={(e) => onZipInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="60025, 60626, 60201"
            inputErrorMsg="Comma-separated 5-digit ZIP codes"
            multiline
            minRows={2}
            maxRows={4}
            sx={multilineInputSx}
          />
        ) : (
          <InputBox
            type="text"
            label="Regional branch"
            name="regionalBranch"
            size="small"
            height="auto"
            value={branchInput}
            onChange={(e) => onBranchInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. regionalbranch-1"
            sx={inputSx}
          />
        )}

        {analyticsError ? (
          <Typography sx={{ fontSize: 11.5, color: '#B91C1C', fontFamily: 'Inter Regular, sans-serif' }}>
            Analytics: {analyticsError}
          </Typography>
        ) : null}

        {analyticsSummary ? (
          <Box
            sx={{
              border: '1px solid #D7E0F2',
              borderRadius: '8px',
              bgcolor: '#F8FAFC',
              px: 1.25,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.75,
              }}
            >
              Branch analytics
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#334155', fontFamily: 'Inter Regular, sans-serif', lineHeight: 1.5 }}>
              {analyticsSummary.zipcodeCount ?? 0} zips · {analyticsSummary.totalCustomers ?? 0} customers ·{' '}
              {analyticsSummary.totalStores ?? 0} stores
            </Typography>
          </Box>
        ) : null}

        {error && (
          <Typography sx={{ fontSize: 12, color: '#B91C1C', fontFamily: 'Inter Regular, sans-serif' }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={loading}
            onClick={onSearch}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon sx={{ fontSize: 18 }} />}
            sx={primaryBtnSx}
          >
            {loading ? 'Searching…' : 'Search'}
          </Button>
          <Button
            variant="outlined"
            disabled={loading && !searchResults?.length}
            onClick={onClear}
            sx={clearBtnSx}
            aria-label="Clear search"
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>

        {notFoundZips?.length > 0 && (
          <NotFoundZipNotice
            notFoundZips={notFoundZips}
            resolvedBy={resolvedBy}
            searchMode={searchMode}
            mappedCount={mappedCount}
          />
        )}

        {searchResults?.length > 0 && (
          <Box
            sx={{
              maxHeight: 200,
              overflowY: 'auto',
              border: '1px solid #D7E0F2',
              borderRadius: '5px',
              bgcolor: '#FAFAFA',
            }}
          >
            <Typography
              sx={{
                px: 1.25,
                py: 0.75,
                fontSize: 10.5,
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: '1px solid #D7E0F2',
              }}
            >
              Coordinates ({searchResults.length})
            </Typography>
            {searchResults.map(({ zip, coords }) => {
              const zipStats = zipStatsMap?.[zip];
              return (
              <Box
                key={zip}
                sx={{
                  px: 1.25,
                  py: 0.85,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  borderBottom: '1px solid #F3F4F6',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <PlaceIcon sx={{ fontSize: 16, color: '#3F3F6E', mt: 0.15, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: 'Inter Bold, sans-serif', color: '#1e293b' }}>
                    {zip}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b', fontFamily: 'Inter Regular, sans-serif' }}>
                    {coords[0].toFixed(5)}, {coords[1].toFixed(5)}
                  </Typography>
                  {zipStats ? (
                    <Typography sx={{ fontSize: 10.5, color: '#475569', fontFamily: 'Inter Regular, sans-serif', mt: 0.25 }}>
                      {zipStats.customerCount ?? 0} customers · {zipStats.storeCount ?? 0} stores
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

const primaryBtnSx = {
  textTransform: 'none',
  fontFamily: 'Inter Bold, sans-serif',
  fontWeight: 700,
  fontSize: 13,
  borderRadius: '5px',
  bgcolor: '#3F3F6E',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#2d2d52', boxShadow: 'none' },
};

const clearBtnSx = {
  minWidth: 44,
  borderRadius: '5px',
  borderColor: '#D7E0F2',
  color: '#3F3F6E',
};

function NotFoundZipNotice({ notFoundZips, resolvedBy, searchMode, mappedCount }) {
  const [showList, setShowList] = useState(false);
  const count = notFoundZips.length;
  const isBranchSearch =
    resolvedBy === 'regional_branch' || searchMode === 'branch';

  const summary = isBranchSearch
    ? `${count} ZIP${count === 1 ? '' : 's'} on this branch's serving list could not be drawn (no boundary polygon in map data). ${mappedCount} ZIP${mappedCount === 1 ? '' : 's'} shown on the map.`
    : `Could not draw ${count} ZIP${count === 1 ? '' : 's'} you searched for — no boundary polygon available.`;

  return (
    <Box
      sx={{
        fontSize: 11.5,
        color: '#B45309',
        fontFamily: 'Inter Regular, sans-serif',
        lineHeight: 1.45,
      }}
    >
      <Typography component="p" sx={{ fontSize: 'inherit', color: 'inherit', mb: count > 5 ? 0.5 : 0 }}>
        {summary}
      </Typography>
      {count > 0 && (
        <Button
          size="small"
          onClick={() => setShowList((v) => !v)}
          sx={{
            textTransform: 'none',
            fontSize: 11,
            p: 0,
            minWidth: 0,
            color: '#B45309',
            fontWeight: 600,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {showList ? 'Hide ZIP list' : `Show ZIP list (${count})`}
        </Button>
      )}
      {showList && (
        <Typography
          component="p"
          sx={{
            fontSize: 10.5,
            color: '#92400E',
            mt: 0.75,
            maxHeight: 72,
            overflowY: 'auto',
            wordBreak: 'break-word',
          }}
        >
          {notFoundZips.join(', ')}
        </Typography>
      )}
    </Box>
  );
}
